import React, { useEffect, useState, useCallback } from "react";
import taskSchema from "../schemas/task.json";
import DynamicForm from "../components/DynamicForm/DynamicForm";
import DataTable from "../components/DataTable/DataTable";
import { apiGet, apiPost, apiPut, apiDelete } from "../services/api";
import { multipartHeaders } from "./app_props";
import { Box, Card, CardContent, Button, Typography } from "@mui/material";
import { usePopup } from "../components/PopupProvider";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { restApiUrl } from "./app_props";
import * as permissions from "./app_props";

export default function TasksPage() {
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const [rows, setRows] = useState([]);
  const SESSION_PAGE_SIZE_KEY = "compliance_ui_page_size";
  const defaultSize = (() => {
    try {
      const s = sessionStorage.getItem(SESSION_PAGE_SIZE_KEY);
      const n = Number(s);
      if (n) return n;
    } catch (e) {
      // ignore
    }
    return 10;
  })();
  const [pageInfo, setPageInfo] = useState({
    page: 0,
    size: defaultSize,
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { showConfirm, showSuccess, showError } = usePopup();

  const buildFilterQuery = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(`filter.${key}`, value);
      }
    });
    return params.toString();
  }, [filters]);

  const load = useCallback(async () => {
    try {
      const query = buildFilterQuery();
      const resp = await apiGet(
        `/api/tasks?page=${pageInfo.page}&size=${pageInfo.size}${query ? `&${query}` : ""}`,
      );
      const content = Array.isArray(resp) ? resp : resp.content || [];
      // Map backend enum values to UI labels for display, but keep raw value on _statusRaw
      const statusMap = {
        PENDING: "Pending",
        IN_PROGRESS: "In Progress",
        COMPLETED: "Completed",
      };
      const mapped = content.map((r) => ({
        ...r,
        _statusRaw: r.status,
        status: statusMap[r.status] || r.status,
      }));
      setRows(mapped);
      // debug: log fetched rows so we can trace why table doesn't update
      // eslint-disable-next-line no-console
      // console.log(
      //   "TasksPage.load() fetched rows:",
      //   mapped,
      //   "resp.totalElements=",
      //   resp && resp.totalElements,
      // );
      if (resp && typeof resp.totalElements !== "undefined") {
        setPageInfo((p) => ({ ...p, total: resp.totalElements }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load tasks", err);
    }
  }, [buildFilterQuery, pageInfo.page, pageInfo.size]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePageSizeChange = useCallback((newSize) => {
    try {
      sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, String(newSize));
    } catch (e) {
      // ignore
    }
    setPageInfo((p) => ({ ...p, page: 0, size: newSize }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPageInfo((p) => ({ ...p, page: newPage }));
  }, []);

  async function handleSave(formValues) {
    try {
      const details = formValues.details || {};
      const payload = {
        instruction: details.instruction || formValues.instruction || "",
        location: details.location,
        type: details.type || null,
        completionDetails: details.completionDetails || null,
        dueDate: details.dueDate || null,
        dateCompleted: details.dateCompleted || null,
        // details.status may be the UI label; map back to backend enum if needed
        status: (() => {
          const rev = {
            Pending: "PENDING",
            "In Progress": "IN_PROGRESS",
            Completed: "COMPLETED",
          };
          if (!details.status) return null;
          return rev[details.status] || details.status;
        })(),
        requirement: details.requirement,
      };
      // include modifiedAt when editing so backend can validate concurrency

      if (details && details.modifiedAt) {
        payload.modifiedAt = details.modifiedAt;
      }
      const existingId = formValues._id || formValues.id || null;
      let saved;
      if (existingId) {
        // backend expects multipart for PUT; send data as FormData even if no files
        const fd = new FormData();
        details.attachments?.forEach((item) => {
          item.file.forEach((f) => {
            fd.append("attachments", f.file);
          });
        });
        fd.append(
          "data",
          new Blob([JSON.stringify(payload)], { type: "application/json" }),
        );
        const resp = await fetch(restApiUrl + `/api/tasks/${existingId}`, {
          method: "PUT",
          headers: multipartHeaders(),
          body: fd,
        });
        if (!resp.ok) throw new Error(await resp.text());
        saved = await resp.json();
      } else {
        saved = await apiPost(`/api/tasks`, payload);
      }
      showSuccess && showSuccess("Task saved");
      load();
      navigate("/tasks");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      showError && showError("Save failed: " + (err.message || err));
    }
  }

  return (
    <div className="h-[89.8vh]">
      <Routes>
        <Route
          index
          element={
            <Box sx={{ m: 1 }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">Tasks</Typography>
                    <div>
                      {user &&
                        [permissions.COMPLIANCE_CAN_ADD_TASKS].some(
                          (authority) =>
                            user.authorities === undefined
                              ? false
                              : user.authorities.includes(authority),
                        ) && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate("/tasks/new")}
                          >
                            Add New Task
                          </Button>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <DataTable
                      schema={taskSchema}
                      rows={rows}
                      onFiltersChange={setFilters}
                      onPageSizeChange={handlePageSizeChange}
                      totalCount={pageInfo.total}
                      currentPage={pageInfo.page}
                      onPageChange={handlePageChange}
                      extraControls={null}
                      onReview={(idx, row) => {
                        const statusMap = {
                          PENDING: "Pending",
                          IN_PROGRESS: "In Progress",
                          COMPLETED: "Completed",
                        };
                        const statusValue =
                          (row._statusRaw && statusMap[row._statusRaw]) ||
                          row.status ||
                          null;
                        const defaultValues = {
                          _id: row.id || row._id || null,
                          details: {
                            location: row.location || null,
                            type: row.type || null,
                            person: row.person || null,
                            instruction:
                              row.instruction || row.description || null,
                            completionDetails: row.completionDetails || null,
                            dateCompleted: row.dateCompleted || null,
                            dueDate: row.dueDate || null,
                            status: statusValue,
                            modifiedAt: row.modifiedAt || null,
                          },
                        };
                        navigate(`/tasks/new`, {
                          state: { defaultValues, reviewOnly: true },
                        });
                      }}
                      onDelete={async (index) => {
                        try {
                          const row = rows[index];
                          const id = row?.id || row?._id || null;
                          if (!id) {
                            showError("Cannot determine task id to delete");
                            return;
                          }
                          const ok = await showConfirm(
                            `Delete task '${row.title || row.summary || id}'? This cannot be undone.`,
                            {
                              title: "Delete Task",
                              confirmText: "Delete",
                              cancelText: "Cancel",
                              severity: "error",
                            },
                          );
                          if (!ok) return;
                          await apiDelete(`/api/tasks/${id}`);
                          showSuccess("Task deleted");
                          load();
                        } catch (err) {
                          // eslint-disable-next-line no-console
                          console.error(err);
                          showError("Delete failed: " + (err.message || err));
                        }
                      }}
                      onEdit={(idx, row) => {
                        const statusMap = {
                          PENDING: "Pending",
                          IN_PROGRESS: "In Progress",
                          COMPLETED: "Completed",
                        };
                        const statusValue =
                          (row._statusRaw && statusMap[row._statusRaw]) ||
                          row.status ||
                          null;
                        const defaultValues = {
                          _id: row.id || row._id || null,
                          details: {
                            location: row.location || null,
                            type: row.type || null,
                            person: row.person || null,
                            instruction:
                              row.instruction || row.description || null,
                            completionDetails: row.completionDetails || null,
                            dateCompleted: row.dateCompleted || null,
                            dueDate: row.dueDate || null,
                            status: statusValue,
                            modifiedAt: row.modifiedAt || null,
                          },
                        };

                        navigate(`/tasks/new`, {
                          state: { defaultValues },
                        });
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
        <Route
          path="new"
          element={
            <Box sx={{ m: 1, maxHeight: "87vh", overflowY: "auto" }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">New Task</Typography>
                  </div>
                  <div
                    style={{ marginTop: 12 }}
                    className="flex justify-center"
                  >
                    <div style={{ width: "100%", maxWidth: 900 }}>
                      <DynamicForm
                        schema={taskSchema}
                        defaultValues={location.state?.defaultValues || {}}
                        reviewOnly={location.state?.reviewOnly === true}
                        onSubmit={handleSave}
                        onCancel={() => navigate("/tasks")}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
        {/* <Route
          path="review"
          element={<ReviewTaskForm onSaved={() => load()} />}
        /> */}
      </Routes>
    </div>
  );
}
